import React, { useState } from 'react';
import { Card, CardBody, Badge, Button } from 'reactstrap';

const TokenStatusCard = ({
  token,
  country,
  onTokenRenewed,
  onManualRenewal,
}) => {
  const [isRenewing, setIsRenewing] = useState(false);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'active':
        return {
          icon: '✅',
          color: 'success',
          label: 'ACTIVE',
          btnClass: 'btn-outline-success',
        };
      case 'warning':
        return {
          icon: '⚠️',
          color: 'warning',
          label: 'EXPIRING',
          btnClass: 'btn-warning',
        };
      case 'critical':
        return {
          icon: '🚨',
          color: 'danger',
          label: 'CRITICAL',
          btnClass: 'btn-danger',
        };
      case 'expired':
        return {
          icon: '❌',
          color: 'dark',
          label: 'EXPIRED',
          btnClass: 'btn-danger',
        };
      default:
        return {
          icon: '❓',
          color: 'secondary',
          label: 'UNKNOWN',
          btnClass: 'btn-secondary',
        };
    }
  };

  const statusConfig = getStatusConfig(token.status);

  const handleRenewToken = async () => {
    setIsRenewing(true);

    try {
      // Automatic OAuth Method
      const renewUrl = `${process.env.REACT_APP_WS_FUNCTION_URL}/facebook/auth/${token.countryId}`;

      // Open in a new window
      const popup = window.open(
        renewUrl,
        'facebook-oauth',
        'width=600,height=700,scrollbars=yes,resizable=yes',
      );

      // Monitor popup closure
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setIsRenewing(false);
          // Reload token data
          onTokenRenewed();
        }
      }, 1000);
    } catch (error) {
      console.error('Error renewing token:', error);
      setIsRenewing(false);
      alert('Error renewing token. Please try again later.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getButtonText = () => {
    if (isRenewing) return '🔄 Renewing...';
    if (token.status === 'expired') return '🚨 Renew Now';
    if (token.status === 'critical') return '⚠️ Renew Now';
    if (token.status === 'warning') return '📅 Renew';
    return '🔄 Renew Token';
  };

  return (
    <Card
      className={`border-left border-${statusConfig.color} h-100`}
      style={{
        borderLeftWidth: '4px',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}>
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className="mb-1">
              {statusConfig.icon} {country.name}
            </h5>
          </div>
          <Badge color={statusConfig.color} className="font-size-12">
            {statusConfig.label}
          </Badge>
        </div>

        <div>
          <div className="mb-2">
            <small className="text-muted d-block">Expiration date</small>
            <span className="font-weight-medium">
              {formatDate(token.expirationDate)}
            </span>
          </div>

          <div className="mb-2">
            <small className="text-muted d-block">Days remaining</small>
            <h4 className={`mb-0 text-${statusConfig.color}`}>
              {token.daysUntilExpiration}
            </h4>
          </div>

          {token.lastUpdated && (
            <div className="mb-3">
              <small className="text-muted d-block">Last updated</small>
              <small>{formatDate(token.lastUpdated)}</small>
            </div>
          )}
        </div>

        <div className="mt-3">
          <Button
            color={statusConfig.btnClass.replace('btn-', '')}
            className="w-100 mb-2"
            onClick={handleRenewToken}
            disabled={isRenewing}>
            {getButtonText()}
          </Button>

          {onManualRenewal && (
            <Button
              color="secondary"
              outline
              className="w-100"
              onClick={() => onManualRenewal(token.countryId)}
              disabled={isRenewing}>
              <i className="bx bx-wrench mr-1"></i>
              Manual Renewal
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default TokenStatusCard;
